import Axios from "../utils/axiosInstance";
import qs from "qs";

export type QuestionType =
  | "text"
  | "checkbox"
  | "multi_select"
  | "single_select"
  | "dropdown"
  | "date_picker";

export interface EventQuestion {
  ID: number;
  eventId: number;
  label: string;
  type: QuestionType;
  options: string[];
  isRequired: boolean;
  order: number;
  CreatedAt?: string;
  UpdatedAt?: string;
  DeletedAt?: string | null;
}

export interface CreateQuestionPayload {
  eventId: number;
  label: string;
  type: QuestionType;
  options?: string[];
  isRequired: boolean;
  order: number;
}

export interface UpdateQuestionPayload {
  eventId: number;
  questionId: number;
  label?: string;
  type?: QuestionType;
  options?: string[];
  isRequired?: boolean;
  order?: number;
}

export interface QuestionAnswer {
  questionId: number;
  answer: string | string[];
}

export interface SubmitAnswersPayload {
  eventId: number;
  guestId: number;
  answers: QuestionAnswer[];
}

export interface GuestAnswerItem {
  ID: number;
  eventId: number;
  guestId: number;
  questionId: number;
  answer: string;
  CreatedAt?: string;
  UpdatedAt?: string;
  DeletedAt?: string | null;
}

// ─── Host question management ────────────────────────────────────────────────

const getQuestions = async (
  eventId: number,
  signal?: AbortSignal,
): Promise<EventQuestion[]> => {
  const res = await Axios.get(
    `event/question/get-event-questions?${qs.stringify({ eventId })}`,
    { signal },
  );
  return res?.data ?? [];
};

const createQuestion = async (
  data: CreateQuestionPayload,
): Promise<EventQuestion> => {
  const res = await Axios.post("event/question/create-event-question", data);
  return res?.data;
};

const updateQuestion = async (
  data: UpdateQuestionPayload,
): Promise<EventQuestion> => {
  const { questionId, ...rest } = data;
  const res = await Axios.put("event/question/update-event-question", {
    ...rest,
    questionId: questionId,
  });
  return res?.data;
};

const deleteQuestion = (eventId: number, questionId: number): Promise<void> => {
  return Axios.delete(
    `event/question/delete-event-question?${qs.stringify({ eventId, questionId })}`,
  );
};

// ─── Guest RSVP flow ─────────────────────────────────────────────────────────

/** Fetch questions visible to a guest before/during RSVP */
const getQuestionsForGuest = async (
  eventId: number,
  signal?: AbortSignal,
): Promise<EventQuestion[]> => {
  const res = await Axios.get(
    `event/question/get-questions-for-guest?${qs.stringify({ eventId })}`,
    { signal },
  );
  return res?.data ?? [];
};

export interface SubmitAnswersResponse {
  code: number;
  status: boolean;
  message?: string;
}

/** Submit a guest's answers (called before/alongside RSVP) */
const submitAnswers = async (
  data: SubmitAnswersPayload,
): Promise<SubmitAnswersResponse> => {
  const res:any = await Axios.post("event/answer/submit-answers", data);
  return res;
};

// ─── Answer fetching ─────────────────────────────────────────────────────────

/** Guest fetches their own answers */
const getGuestAnswers = async (
  eventId: number,
  guestId: number,
  signal?: AbortSignal,
): Promise<GuestAnswerItem[]> => {
  const res = await Axios.get(
    `event/answer/get-guest-answers?${qs.stringify({ eventId, guestId })}`,
    { signal },
  );
  return res?.data ?? [];
};

/** Host fetches a specific guest's answers */
const getGuestAnswersByHost = async (
  eventId: number,
  guestId: number,
  signal?: AbortSignal,
): Promise<GuestAnswerItem[]> => {
  const res = await Axios.get(
    `event/answer/get-guest-answers-by-host?${qs.stringify({ eventId, guestId })}`,
    { signal },
  );
  return res?.data ?? [];
};

export const guestQuestionsService = {
  getQuestions,
  createQuestion,
  updateQuestion,
  deleteQuestion,
  getQuestionsForGuest,
  submitAnswers,
  getGuestAnswers,
  getGuestAnswersByHost,
};
